import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import { persistUnsupportedSchoolsInterest } from '../../actions/index';
import LoadingButton from '../../components/LoadingButton';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import { monthOptions, yearOptionsAscending } from '../../lib/dateRanges';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const placeholder = label => ({ value: '', label });

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const isInvalidDate = (year, month) => {
  if (month === '' || year === '') {
    return true;
  }

  const currentDate = new Date(Date.now());
  const date = new Date(year, month);
  const dateAlreadyPast = currentDate > date;

  return isNaN(date) || dateAlreadyPast;
};

const UnsupportedSchoolPage = ({
  studyStartDate,
  selectedSchool,
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchUpdateUnsupportedSchoolsInterest,
}) => {
  const { label: schoolName } = selectedSchool;
  const [month, setMonth] = useState(studyStartDate.month);
  const [year, setYear] = useState(studyStartDate.year);
  const [isFilledIn, setIsFilledIn] = useState(false);
  const [error, setError] = useState(null);

  const changeMonth = event => {
    setMonth(event.target.value);
  };

  const changeYear = event => {
    setYear(event.target.value);
  };

  useEffect(() => {
    const areFieldsFilledIn = month && year;

    setIsFilledIn(areFieldsFilledIn);
  }, [month, year]);

  const isInvalid = useMemo(() => {
    if (isInvalidDate(year, month)) {
      setError('Please enter a valid date.');
      return true;
    }

    setError(null);
    return false;
  }, [year, month]);

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) return;

    analytics.sendEvent('Click - Next', 'unsupported-school');

    const payload = payloadFactory.createAddPageDataPayload(
      'studyStartDate',
      {
        month,
        year,
      },
      'studyStartDate',
      fullDateFormat(new Date(year, month)),
    );

    dispatchAddPageData(payload);
    dispatchUpdateUnsupportedSchoolsInterest();
    dispatchWizardNav({ dir: 'fwd' });
  };

  const shouldShowError = isFilledIn && isInvalid;

  const inputErrorClass = shouldShowError ? 'has-error' : '';

  const renderError = () => {
    if (shouldShowError) {
      return <div className="row-spacer text-error">{error}</div>;
    }

    return null;
  };

  return (
    <PageLayout title={`We're currently unable to support courses at ${schoolName}`}>
      <form onSubmit={handleSubmit}>
        <div className="row-spacer">
          <p>
            Tell us when you plan to study, and we'll let you know if we support courses at this
            school in the future.
          </p>
        </div>
        <div className="row">
          <div className="col col--6/16-small col--4/16-medium">
            <Select
              className={inputErrorClass}
              id="study-start-month"
              value={month}
              options={monthOptions(monthNames, placeholder)}
              onChange={changeMonth}
            />
          </div>

          <div className="col col--auto">
            <Select
              className={inputErrorClass}
              id="study-start-year"
              value={year}
              options={yearOptionsAscending(0, 5, placeholder)}
              onChange={changeYear}
            />
          </div>

          <div className="col col--auto">
            <LoadingButton type="submit" isDisabled={isInvalid}>
              Submit
            </LoadingButton>
          </div>
        </div>

        {renderError()}
      </form>
    </PageLayout>
  );
};

UnsupportedSchoolPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  selectedSchool: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  studyStartDate: PropTypes.shape({
    month: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }),
  dispatchUpdateUnsupportedSchoolsInterest: PropTypes.func.isRequired,
};

UnsupportedSchoolPage.defaultProps = {
  studyStartDate: { month: '', year: '' },
};

export const mapStateToProps = reduxStore => ({
  selectedSchool: reduxStore.builder.ui.school,
  studyStartDate: reduxStore.builder.ui.studyStartDate,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchUpdateUnsupportedSchoolsInterest: persistUnsupportedSchoolsInterest,
};

export { UnsupportedSchoolPage };

export default connect(mapStateToProps, mapDispatchToProps)(UnsupportedSchoolPage);
